import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, CardHeader, Spinner, Alert, ButtonGroup, Button } from 'reactstrap';
import ReactECharts from 'echarts-for-react';
import axios from 'axios';
import LogoSpinner from '../../Components/Common/LogoSpinner';

const MatchAnalytics = ({ dateRange, groupBy = 'daily' }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [metadata, setMetadata] = useState({
        total_matches: 0,
        average_matches: 0,
        peak_period: '',
        growth_rate: 0
    });
    const [lastUpdated, setLastUpdated] = useState(null);
    const [activeSeries, setActiveSeries] = useState({
        matches: true,
        success_rate: true,
        average_time: true,
        new_users: true
    });

    // Toggle series visibility
    const toggleSeries = (series) => {
        setActiveSeries(prev => ({
            ...prev,
            [series]: !prev[series]
        }));
    };

    // Function to fetch data from API (as fallback)
    const fetchDataFromAPI = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const authToken = JSON.parse(sessionStorage.getItem('authUser'))?.token;
            if (!authToken) {
                throw new Error('Authentication token not found');
            }

            const response = await axios.get(`${process.env.REACT_APP_API_URL1}analytics/dashboard/match-analytics`, {
                params: {
                    start_date: dateRange?.start?.toISOString(),
                    end_date: dateRange?.end?.toISOString(),
                    group_by: groupBy
                },
                headers: {
                    'Authorization': `${authToken}`
                }
            });

            if (response.data) {
                console.log('Match Analytics API Response:', response.data);
                setChartData(response.data.metrics || []);
                
                // Set metadata based on grouping type
                const metadataValue = response.data.metadata || {};
                setMetadata({
                    total_matches: metadataValue.total_matches || 0,
                    average_matches: getAverageMatches(metadataValue, groupBy),
                    peak_period: getPeakPeriod(metadataValue, groupBy),
                    growth_rate: metadataValue.growth_rate || 0
                });
                
                setLastUpdated(new Date().toLocaleString());
            } else {
                console.warn('API returned unsuccessful response:', response.data);
                setError('Failed to fetch match analytics data. Please try again later.');
            }
        } catch (error) {
            console.error('Error fetching match analytics data:', error);
            setError(`Failed to fetch match analytics data: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Function to check and use WebSocket data if available
    const checkWebSocketData = () => {
        if (window.dashboardData && window.dashboardData.matchAnalytics) {
            console.log('Using WebSocket data for match analytics');
            const wsData = window.dashboardData.matchAnalytics;
            setChartData(wsData.metrics || []);
            
            const metadataValue = wsData.metadata || {};
            setMetadata({
                total_matches: metadataValue.total_matches || 0,
                average_matches: getAverageMatches(metadataValue, groupBy),
                peak_period: getPeakPeriod(metadataValue, groupBy),
                growth_rate: metadataValue.growth_rate || 0
            });
            
            setLastUpdated(new Date().toLocaleString());
            setLoading(false);
            return true;
        }
        return false;
    };

    useEffect(() => {
        // First check if WebSocket data is available
        const hasWebSocketData = checkWebSocketData();
        
        // If no WebSocket data, fetch from API as fallback
        if (!hasWebSocketData) {
            fetchDataFromAPI();
        }
        
        // Set up event listener for WebSocket updates
        const handleMatchAnalyticsUpdate = (data) => {
            console.log('Received match analytics update from WebSocket:', data);
            setChartData(data.metrics || []);
            
            const metadataValue = data.metadata || {};
            setMetadata({
                total_matches: metadataValue.total_matches || 0,
                average_matches: getAverageMatches(metadataValue, groupBy),
                peak_period: getPeakPeriod(metadataValue, groupBy),
                growth_rate: metadataValue.growth_rate || 0
            });
            
            setLastUpdated(new Date().toLocaleString());
            setLoading(false);
        };
        
        // Add event listener
        if (window.dashboardEvents) {
            window.dashboardEvents.on('dashboard:matchAnalytics', handleMatchAnalyticsUpdate);
        }
        
        // Clean up
        return () => {
            if (window.dashboardEvents) {
                window.dashboardEvents.off('dashboard:matchAnalytics', handleMatchAnalyticsUpdate);
            }
        };
    }, [dateRange, groupBy]);

    // Helper function to get the average matches based on grouping
    const getAverageMatches = (metadata, groupBy) => {
        if (groupBy === 'daily') return metadata.average_daily_matches;
        if (groupBy === 'weekly') return metadata.average_weekly_matches;
        if (groupBy === 'monthly') return metadata.average_monthly_matches;
        if (groupBy === 'yearly') return metadata.average_yearly_matches;
        return 0;
    };

    // Helper function to get the peak period based on grouping
    const getPeakPeriod = (metadata, groupBy) => {
        if (groupBy === 'daily') return metadata.peak_day;
        if (groupBy === 'weekly') return metadata.peak_week;
        if (groupBy === 'monthly') return metadata.peak_month;
        if (groupBy === 'yearly') return metadata.peak_year;
        return '';
    };

    const getOption = () => {
        if (!chartData.length) return {};
        
        // Extract data for each series
        const xAxisData = chartData.map(item => {
            if (groupBy === 'daily') return item.date;
            if (groupBy === 'weekly') return item.week_label;
            if (groupBy === 'monthly') return item.month;
            if (groupBy === 'yearly') return item.year;
            return '';
        });
        
        const matchesData = chartData.map(item => item.matches);
        const successRateData = chartData.map(item => item.success_rate);
        const avgTimeData = chartData.map(item => item.average_time);
        const newUsersData = chartData.map(item => item.new_users);
        
        // Find max values for scaling
        const maxMatches = Math.max(...matchesData, 10);
        const maxNewUsers = Math.max(...newUsersData, 10);
        const maxYAxis1 = Math.max(maxMatches, maxNewUsers);
        
        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                    crossStyle: {
                        color: '#999'
                    }
                },
                formatter: function(params) {
                    let tooltip = `<strong>${params[0].axisValue}</strong><br/>`;
                    
                    params.forEach(param => {
                        const value = param.value;
                        const marker = param.marker;
                        const seriesName = param.seriesName;
                        
                        if (seriesName === 'Success Rate') {
                            tooltip += `${marker} ${seriesName}: ${value}%<br/>`;
                        } else if (seriesName === 'Avg Time') {
                            tooltip += `${marker} ${seriesName}: ${value} sec<br/>`;
                        } else {
                            tooltip += `${marker} ${seriesName}: ${value}<br/>`;
                        }
                    });
                    
                    return tooltip;
                }
            },
            legend: {
                data: ['Matches', 'Success Rate', 'Avg Time', 'New Users'],
                top: 25,
                selected: activeSeries
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true
            },
            toolbox: {
                feature: {
                    dataZoom: {
                        yAxisIndex: 'none'
                    },
                    restore: {},
                    saveAsImage: {}
                }
            },
            xAxis: {
                type: 'category',
                data: xAxisData,
                axisPointer: {
                    type: 'shadow'
                },
                axisLabel: {
                    rotate: xAxisData.length > 12 ? 45 : 0,
                    interval: xAxisData.length > 20 ? 'auto' : 0
                }
            },
            yAxis: [
                {
                    type: 'value',
                    name: 'Count',
                    min: 0,
                    max: Math.ceil(maxYAxis1 * 1.1 / 100) * 100,
                    position: 'left',
                    axisLabel: {
                        formatter: '{value}'
                    }
                },
                {
                    type: 'value',
                    name: 'Rate/Time',
                    min: 0,
                    max: 100,
                    position: 'right',
                    axisLabel: {
                        formatter: '{value}'
                    }
                }
            ],
            series: [
                {
                    name: 'Matches',
                    type: 'bar',
                    barWidth: '20%',
                    yAxisIndex: 0,
                    itemStyle: {
                        color: '#4CAF50',
                        borderRadius: [4, 4, 0, 0]
                    },
                    data: matchesData
                },
                {
                    name: 'Success Rate',
                    type: 'line',
                    yAxisIndex: 1,
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 8,
                    lineStyle: {
                        width: 3,
                        shadowColor: 'rgba(0,0,0,0.3)',
                        shadowBlur: 10,
                        shadowOffsetY: 8
                    },
                    itemStyle: {
                        color: '#FFC107'
                    },
                    data: successRateData
                },
                {
                    name: 'Avg Time',
                    type: 'line',
                    yAxisIndex: 1,
                    smooth: true,
                    symbol: 'triangle',
                    symbolSize: 8,
                    lineStyle: {
                        width: 3,
                        type: 'dashed'
                    },
                    itemStyle: {
                        color: '#9C27B0'
                    },
                    data: avgTimeData
                },
                {
                    name: 'New Users',
                    type: 'bar',
                    barWidth: '20%',
                    yAxisIndex: 0,
                    itemStyle: {
                        color: '#2196F3',
                        borderRadius: [4, 4, 0, 0]
                    },
                    data: newUsersData
                }
            ]
        };
    };

    // Get the title based on grouping type
    const getTitle = () => {
        if (groupBy === 'daily') return 'Daily Match Analytics';
        if (groupBy === 'weekly') return 'Weekly Match Analytics';
        if (groupBy === 'monthly') return 'Monthly Match Analytics';
        if (groupBy === 'yearly') return 'Yearly Match Analytics';
        return 'Match Analytics';
    };

    // Get the period label based on grouping type
    const getPeriodLabel = () => {
        if (groupBy === 'daily') return 'Day';
        if (groupBy === 'weekly') return 'Week';
        if (groupBy === 'monthly') return 'Month';
        if (groupBy === 'yearly') return 'Year';
        return 'Period';
    };

    return (
        <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
                <div>
                    <h4 className="card-title mb-0">{getTitle()}</h4>
                    {metadata && (
                        <p className="text-muted small mt-1 mb-0">
                            Total Matches: {metadata.total_matches.toLocaleString()} | 
                            Avg {getPeriodLabel()}: {metadata.average_matches.toLocaleString()} | 
                            Peak {getPeriodLabel()}: {metadata.peak_period} | 
                            Growth Rate: {metadata.growth_rate.toFixed(1)}%
                        </p>
                    )}
                </div>
            </CardHeader>
            <CardBody>
                {loading ? (
                    <div className="text-center py-5">
                        <LogoSpinner message="Loading match analytics..." />
                    </div>
                ) : error ? (
                    <div className="text-center py-5 text-danger">
                        <i className="ri-error-warning-line fs-3 mb-3 d-block"></i>
                        {error}
                    </div>
                ) : chartData.length > 0 ? (
                    <ReactECharts 
                        option={getOption()} 
                        className="header-chart" 
                        style={{ height: "350px" }}
                        opts={{ renderer: 'svg' }}
                    />
                ) : (
                    <div className="text-center py-5">
                        <p className="text-muted">No match data available for the selected period.</p>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default MatchAnalytics; 